import * as React from "react";
import { Action } from "redux";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import * as moment from "moment";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { clearQuestionnaire, submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { goToParent } from "@teamprevent/common/routing";
import { ImmunisationTaskType } from "@teamprevent/common/consultations/models";
import { toast } from "@teamprevent/common/toasts";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { FollowUpTask, FollowUpTasks } from "../../immunisationReview/components/FollowUpTasks";
import { FollowUpTaskModel } from "../../immunisationReview/model";
import { OhCase } from "../../crud/ohCase";
import { ApplicationState } from "../../applicationState";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { ohCaseSelector as caseSelector } from "../../crud/ohCase/selectors";
import { submitImmunisationBloodTestConsultation } from "../actions";
import { ChaseImmunisationBloodTestResultsTask } from "../../task/taskTypes";
import { loadImmunisationTypeTasksOfType } from "../../crud/immunisationTypeTask/actions";
import { validateConfidentialitySection } from "../../consultations/components/confidentialitySectionValidation";

import { BloodTestConsultation, QUESTIONNAIRE_KEY } from "./BloodTestConsultation";

interface StateProps {
    ohCase: OhCase;
    taskId: string;
    customerId: string;
}

interface DispatchProps {
    submitQuestionnaire: (
        ohCaseId: string,
        taskId: string,
        customerId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationTypeTaskIds: string[]) => void;
    submitInvalidQuestionnaire: () => void;
    navigateBack: () => void;
    loadImmunisationTypeTasks: () => void;
    clearQuestionnaire: () => void;
}

interface State {
    tasks: FollowUpTask[];
    followUpTaskRestrictions: string[];
    submitted: boolean;
    immunisationTypeTaskIds: string[];
    nextTasksUpdated: boolean;
    updateTasks: FollowUpTask[];
    mantouxReadingChecked: boolean;
}

type Props = StateProps & DispatchProps;

export class ImmunisationBloodTestConsultationUnconnected extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            followUpTaskRestrictions: [ChaseImmunisationBloodTestResultsTask.type],
            submitted: false,
            tasks: [{ task: { taskType: null, immunisationTypeTasks: [] }, isValid: false, isHidden: false }],
            immunisationTypeTaskIds: [],
            nextTasksUpdated: false,
            updateTasks: [],
            mantouxReadingChecked: false
        };
        this.onChangeTasks = this.onChangeTasks.bind(this);
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
    }

    public render() {

        const updateTasks = this.state.updateTasks.filter(ut => !ut.isHidden);

        const tasksSection =
            this.props.ohCase && this.props.ohCase.immunisations && (
                <FollowUpTasks
                    immunisationTypes={this.props.ohCase.immunisations.map(i => i.typeId)}
                    onChange={this.onChangeTasks}
                    showErrors={this.state.submitted}
                    tasks={this.state.tasks}
                    restrictToTaskTypes={this.state.followUpTaskRestrictions}
                    updateTasks={updateTasks}
                    forceTasksUpdate={this.state.nextTasksUpdated}
                    onTasksUpdate={this.setNextTasksUpdated}
                    multiple={false}
                    assignmentOptionRequired={true}
                    showImmunisationNameAndType={true}
                />
            );
        return (
            <BloodTestConsultation
                submitQuestionnaire={this.submitQuestionnaire}
                tasksSection={tasksSection}
                onImmunisationTypesChanged={this.onImmunisationTypeTasksChanged}
                tasksIsValid={!this.state.tasks.some(t => !t.isValid)}
                toggleRestrictToTaskTypes={this.toggleRestrictToTaskTypes}
                onChangeTasks={this.onChangeTasks}
            />
        );
    }

    public toggleRestrictToTaskTypes = (taskType: string, mantouxReadingChecked: boolean) => {
        const containsTaskType: boolean = this.state.followUpTaskRestrictions.some(restriction => restriction === taskType);

        let followUpTaskRestrictions = [...this.state.followUpTaskRestrictions];
        if (containsTaskType) {
            followUpTaskRestrictions = followUpTaskRestrictions.filter(r => r !== taskType);
        }
        else {
            followUpTaskRestrictions.push(taskType);
        }

        let nextTasksUpdated = false;

        if (this.state.mantouxReadingChecked && !mantouxReadingChecked) {
            // Restore tasks prior to checking of Mantoux box
            nextTasksUpdated = true;
        }

        this.setState({
            followUpTaskRestrictions,
            tasks: [{ task: { taskType: null, immunisationTypeTasks: [] }, isValid: false, isHidden: false }],
            mantouxReadingChecked,
            nextTasksUpdated
        });
    };

    public componentDidMount = () => {
        this.props.clearQuestionnaire();
        this.props.loadImmunisationTypeTasks();
    };

    private onChangeTasks(tasks: FollowUpTask[]) {
        this.setState({ tasks });
    }
    private setNextTasksUpdated = (nextTasksUpdated: boolean) => {
        this.setState({ nextTasksUpdated });
    };

    private onImmunisationTypeTasksChanged = (immunisationTypeTaskIds: string[], noBloodTestsTakenToday: boolean) => {
        let nextTasksUpdated = false;
        const updateTasks: { task: FollowUpTaskModel; isValid: boolean; isHidden: boolean }[] = [
            ...this.state.updateTasks.map(ut => ({
                ...ut,
                isHidden: noBloodTestsTakenToday
            }))
        ];

        if (immunisationTypeTaskIds.some(i => this.state.immunisationTypeTaskIds.indexOf(i) < 0) ||
            this.state.immunisationTypeTaskIds.some(i => immunisationTypeTaskIds.indexOf(i) < 0)) {
            updateTasks.splice(0, updateTasks.length);
            nextTasksUpdated = !this.state.mantouxReadingChecked || !noBloodTestsTakenToday;
            immunisationTypeTaskIds.forEach(() => {
                updateTasks.push({
                    task: {
                        taskType: "ChaseImmunisationBloodTestResultsTask",
                        immunisationTypeTasks: [],
                        dueDate: moment.utc().clone().add(5, "days")
                    },
                    isValid: false,
                    isHidden: noBloodTestsTakenToday
                });
            });
        } else if (!noBloodTestsTakenToday) {
            nextTasksUpdated = !noBloodTestsTakenToday;
        }

        this.setState({ immunisationTypeTaskIds, nextTasksUpdated, updateTasks });
    };

    private async submitQuestionnaire(questions: AnsweredQuestionWithId[]) {
        if (this.state.tasks.length > 0 && this.state.tasks.some(t => !t.isValid)) {
            return this.props.submitInvalidQuestionnaire();
        }

        const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

        const validationResult = validateConfidentialitySection(questions);

        if (immunisationWorkflowImprovementsPt1 && !validationResult.isValid) {
            toast.warning(validationResult.error);
            return this.props.submitInvalidQuestionnaire();
        }

        const tasks = this.state.tasks.filter(t => !t.isHidden).map(t => t.task);
        await this.props.submitQuestionnaire(
            this.props.ohCase.id,
            this.props.taskId,
            this.props.customerId,
            questions,
            tasks,
            this.state.immunisationTypeTaskIds);
        this.props.navigateBack();
    }
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    customerId: customerIdSelector(state),
    ohCase: caseSelector(state),
    taskId: state.router.params.taskId
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => ({
    submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(QUESTIONNAIRE_KEY, true)),
    submitQuestionnaire: (
        ohCaseId: string,
        taskId: string,
        customerId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationTypeTaskIds: string[]) => {
        dispatch(submitImmunisationBloodTestConsultation(ohCaseId, taskId, customerId, questions, QUESTIONNAIRE_KEY, tasks, immunisationTypeTaskIds));
    },
    navigateBack: () => dispatch(goToParent()),
    loadImmunisationTypeTasks: () => dispatch(loadImmunisationTypeTasksOfType(ImmunisationTaskType.BloodTest)),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(QUESTIONNAIRE_KEY))
});

export const ImmunisationBloodTestConsultation = connect(mapStateToProps, mapDispatchToProps)(ImmunisationBloodTestConsultationUnconnected);
